import { useCallback, useEffect, useState } from 'react'
import api, { apiErrorMessage } from './client'
import type { Project, ProjectShare, Simulation } from './types'

interface Query<T> {
  data: T | undefined
  loading: boolean
  error: string
  reload: () => void
}

function useGet<T>(url: string): Query<T> {
  const [tick, setTick] = useState(0)
  // Result is tagged with the request it answers, so loading/error are derived
  // instead of being reset synchronously inside the effect.
  const [result, setResult] = useState<{ key: string; url: string; data?: T; error: string }>()
  const key = `${url}#${tick}`

  useEffect(() => {
    let active = true
    api
      .get<T>(url)
      .then((res) => active && setResult({ key, url, data: res.data, error: '' }))
      .catch(
        (err) =>
          active &&
          setResult((prev) => ({
            key,
            url,
            // keep data only when reloading the same resource, never across URLs
            data: prev?.url === url ? prev.data : undefined,
            error: apiErrorMessage(err),
          })),
      )
    return () => {
      active = false
    }
  }, [url, key])

  const reload = useCallback(() => setTick((t) => t + 1), [])
  const current = result?.key === key
  return {
    data: result?.url === url ? result.data : undefined,
    loading: !current,
    error: current ? result.error : '',
    reload,
  }
}

export const useSimulations = () => useGet<Simulation[]>('/simulations')
export const useSimulation = (id: string | number) => useGet<Simulation>(`/simulations/${id}`)
export const useProjects = () => useGet<Project[]>('/projects')
export const useProject = (id: string | number) => useGet<Project>(`/projects/${id}`)
export const useProjectShares = (id: string | number) =>
  useGet<ProjectShare[]>(`/projects/${id}/shares`)
